// import { motion } from "framer-motion";
import { BookOpen, Award, Users, Briefcase, ChevronDown } from "lucide-react";
import { FaLinux } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Syllabus topics with images
const topics = [
  {
    title: "Introduction to Linux & Installation",
    img: "https://cdn-icons-png.flaticon.com/512/25/25231.png",
  },
  {
    title: "Basic Commands & File System Navigation",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910764.png",
  },
  {
    title: "File Permissions & Ownership",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828640.png",
  },
  {
    title: "Process Management",
    img: "https://cdn-icons-png.flaticon.com/512/124/124528.png",
  },
  {
    title: "Shell Scripting Fundamentals",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828595.png",
  },
  {
    title: "User & Group Management",
    img: "https://cdn-icons-png.flaticon.com/512/1077/1077114.png",
  },
  {
    title: "Package Management (apt, yum, dnf)",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828817.png",
  },
  {
    title: "Networking & Services",
    img: "https://cdn-icons-png.flaticon.com/512/597/597177.png",
  },
  {
    title: "System Monitoring & Logs",
    img: "https://cdn-icons-png.flaticon.com/512/1828/1828843.png",
  },
  {
    title: "Linux Security Basics",
    img: "https://cdn-icons-png.flaticon.com/512/565/565547.png",
  },
  {
    title: "Server Administration",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910731.png",
  },
  {
    title: "Project: Deploying a Web Server on Linux",
    img: "https://cdn-icons-png.flaticon.com/512/2910/2910757.png",
  },
];

const sections = [
  {
    icon: <BookOpen className="w-8 h-8 text-blue-600 animate-bounce" />,
    title: "Course Overview",
    description:
      "Master Linux from scratch. Learn commands, file systems, process management, networking, and shell scripting. Suitable for beginners and professionals.",
    img: "https://upload.wikimedia.org/wikipedia/commons/3/35/Tux.svg",
    color: "from-blue-100 to-blue-50",
  },
  {
    icon: <Award className="w-8 h-8 text-yellow-600 animate-pulse" />,
    title: "Key Features",
    description:
      "Hands-on labs, real-world projects, quizzes, interview prep, and certification upon completion.",
    img: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    color: "from-yellow-100 to-yellow-50",
  },
  {
    icon: <Users className="w-8 h-8 text-purple-600 animate-bounce" />,
    title: "Instructor",
    description:
      "Delivered by industry experts with 10+ years of Linux and DevOps experience. Get mentorship and career guidance.",
    img: "https://cdn-icons-png.flaticon.com/512/219/219970.png",
    color: "from-purple-100 to-purple-50",
  },
  {
    icon: <Briefcase className="w-8 h-8 text-red-600 animate-shake" />,
    title: "Career Opportunities",
    description:
      "Linux skills open doors to roles such as System Administrator, DevOps Engineer, Cloud Engineer, and Security Specialist.",
    img: "https://cdn-icons-png.flaticon.com/512/3135/3135673.png",
    color: "from-red-100 to-red-50",
  },
];
const faqs = [
  {
    question: "Do I need prior Linux knowledge?",
    answer: "No, this course starts from the basics and gradually advances.",
  },
  {
    question: "Is there certification?",
    answer:
      "Yes, you’ll receive an industry-recognized certificate upon completion.",
  },
  {
    question: "Are there hands-on projects?",
    answer:
      "Absolutely! You’ll complete real-world projects, including setting up servers.",
  },
  {
    question: "Which tools will I learn?",
    answer: "Shell, Git, Vim/VSCode, Apache/Nginx, and monitoring tools.",
  },
];

export default function Linux() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return (
    <div>
      <section
        className="relative bg-cover bg-center text-white py-24 text-center overflow-hidden"
        style={{
          backgroundImage:
            "url('https://wallpapers.com/images/hd/tux-penguin-linux-desktop-digital-art-421rfyw5668iya04.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-indigo-900/70"></div>

        <div className="relative z-10">
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-4"
            data-aos="fade-down"
          >
            Linux Mastery Course
          </h1>
          <p
            className="text-lg md:text-xl opacity-90 max-w-3xl mx-auto mb-8"
            data-aos="fade-up"
          >
            Learn Linux like a pro with interactive lessons, practical projects,
            and expert mentorship. Get certified and boost your career in IT and
            DevOps.
          </p>
          <Link
            to="/contact"
            data-aos="flip-left"
            className="bg-white text-blue-700 px-10 py-2 font-semibold rounded-xl shadow-xl hover:bg-gray-100 transition-transform hover:scale-110 inline-block"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Syllabus */}

      {/* Syllabus */}
      <div className="max-w-5xl mx-auto my-12">
        <h2 className="text-4xl font-bold text-center text-black mb-8">
          Course Syllabus
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-3">
          {topics.map((topic, index) => {
            const gradients = [
              "from-pink-50 to-pink-100",
              "from-blue-50 to-blue-100",
              "from-green-50 to-green-100",
              "from-yellow-50 to-yellow-100",
              "from-purple-50 to-purple-100",
              "from-indigo-50 to-indigo-100",
              "from-teal-50 to-teal-100",
              "from-orange-50 to-orange-100",
              "from-cyan-50 to-cyan-100",
              "from-rose-50 to-rose-100",
              "from-lime-50 to-lime-100",
              "from-fuchsia-50 to-fuchsia-100",
            ];
            const gradient = gradients[index % gradients.length];

            return (
              <motion.div
                key={index}
                data-aos={index % 2 === 0 ? "flip-left" : "flip-right"}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-gradient-to-r ${gradient} p-4 rounded-2xl shadow-md hover:shadow-xl flex items-center gap-4 cursor-pointer`}
              >
                <motion.img
                  src={topic.img}
                  alt={topic.title}
                  className="w-12 h-12 rounded-lg"
                  animate={{
                    rotate: [0, 10, -10, 0],
                    y: [0, -5, 0, 5, 0],
                    scale: [1, 1.2, 1, 1.2, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "loop",
                    ease: "easeInOut",
                  }}
                />
                <div>
                  <span className="font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-md mb-1 inline-block">
                    Module {index + 1}
                  </span>
                  <h3 className="text-black font-semibold">{topic.title}</h3>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto p-4">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 1 : -1 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            viewport={{ once: true }}
          >
            <div
              className={`rounded-2xl shadow-lg hover:shadow-2xl transition p-6 bg-gradient-to-br ${section.color} flex flex-col items-center text-center`}
            >
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop",
                }}
                className="flex items-center mb-4"
              >
                {section.icon}
                <h2 className="ml-3 text-xl font-semibold text-black">
                  {section.title}
                </h2>
              </motion.div>
              <img
                src={section.img}
                alt={section.title}
                className="w-20 h-20 mb-4"
              />
              <p className="text-black">{section.description}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Why Learn Linux Section */}
      <div className="max-w-5xl mx-auto my-16">
        <h2 className="text-4xl font-bold text-center text-black mb-6">
          Why Learn Linux?
        </h2>
        <p className="text-black text-center mb-8">
          Linux is the backbone of servers, cloud platforms, and DevOps
          pipelines. Learning Linux opens up opportunities in system
          administration, cloud computing, cybersecurity, and software
          development.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
          {[
            {
              icon: "🖥️",
              text: "Gain hands-on experience with Linux commands, shell scripting, and system management.",
              color: "from-blue-400 to-blue-200",
            },
            {
              icon: "📂",
              text: "Work on real-world projects and build practical skills for IT careers.",
              color: "from-green-400 to-green-200",
            },
            {
              icon: "🔒",
              text: "Understand server administration, networking, and security essentials.",
              color: "from-red-400 to-red-200",
            },
            {
              icon: "🎓",
              text: "Prepare for certifications and increase your career growth opportunities.",
              color: "from-purple-400 to-purple-200",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`p-6 rounded-xl shadow-md flex items-start gap-4 cursor-pointer hover:shadow-lg transition bg-gradient-to-r ${item.color}`}
            >
              <span className="text-3xl">{item.icon}</span>
              <p className="text-black font-medium">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto my-16">
        <h2 className="text-4xl font-bold text-center text-black mb-6">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4 p-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg overflow-hidden"
            >
              <button
                className="w-full flex justify-between items-center p-4 text-left cursor-pointer focus:outline-none"
                onClick={() => toggle(index)}
              >
                <span className="font-semibold text-black">{faq.question}</span>
                <motion.span
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-black" />
                </motion.span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 text-black border-t border-gray-200"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Footer Enroll */}
      <div className="text-center mt-12">
        <motion.div
          whileHover={{ scale: 1.1, rotate: 2 }}
          whileTap={{ scale: 0.95 }}
          className="inline-block"
        >
          <Link
            to="/contact"
            className="px-8 py-3 text-lg rounded-2xl shadow-md bg-blue-600 text-white hover:bg-blue-700 transition font-semibold mb-5 inline-block"
          >
            Enroll Now
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
